/**
 * 損益計算の主要フェーズを計測する軽量 profiler を提供する。
 * Bun と browser の両方で使えるよう、`performance.now()` ベースでイベントを蓄積する。
 */

export type AccountingProfileEvent = {
  readonly name: string
  readonly startedAt: number
  readonly durationMs: number
  readonly metadata?: Record<string, unknown>
}

export type AccountingProfiler = {
  measure<T>(
    name: string,
    fn: () => Promise<T>,
    metadata?: Record<string, unknown>,
  ): Promise<T>
  measureSync<T>(
    name: string,
    fn: () => T,
    metadata?: Record<string, unknown>,
  ): T
  mark(name: string, metadata?: Record<string, unknown>): void
  events(): readonly AccountingProfileEvent[]
}

/**
 * profiling 無効時に使う no-op profiler。
 */
export const noopAccountingProfiler: AccountingProfiler = {
  async measure(_name, fn) {
    return await fn()
  },
  measureSync(_name, fn) {
    return fn()
  },
  mark() {},
  events() {
    return []
  },
}

/**
 * 計測イベントをメモリ上へ蓄積する profiler を生成する。
 * @returns `measure` / `mark` で取得したイベントを返す profiler。
 */
export const createAccountingProfiler = (): AccountingProfiler => {
  const eventList: AccountingProfileEvent[] = []

  return {
    async measure<T>(
      name: string,
      fn: () => Promise<T>,
      metadata?: Record<string, unknown>,
    ): Promise<T> {
      const startedAt = performance.now()

      try {
        return await fn()
      } finally {
        eventList.push({
          name,
          startedAt,
          durationMs: performance.now() - startedAt,
          metadata,
        })
      }
    },
    measureSync<T>(
      name: string,
      fn: () => T,
      metadata?: Record<string, unknown>,
    ): T {
      const startedAt = performance.now()

      try {
        return fn()
      } finally {
        eventList.push({
          name,
          startedAt,
          durationMs: performance.now() - startedAt,
          metadata,
        })
      }
    },
    mark(name: string, metadata?: Record<string, unknown>): void {
      eventList.push({
        name,
        startedAt: performance.now(),
        durationMs: 0,
        metadata,
      })
    },
    events(): readonly AccountingProfileEvent[] {
      return [...eventList]
    },
  }
}
